/**
 * Quick View + Toast — Moon Moon Agency
 *
 * QuickViewManager : intercepte les clicks sur [data-quick-add] des cards.
 *   - 1 variante → POST /cart/add.js direct → toast
 *   - N variantes → ouvre le panel + fetch /products/<handle>?view=quick-view (cache)
 *     puis intercepte le submit form → POST /cart/add.js → toast + close panel
 *   - Changement de variante (couleurs indispo comprises, comme le PDP) → fetch ?variant=ID et
 *     remplace les zones [data-quick-view-reload] (prix, couleur, CTA, stock/livraison)
 *   - Prefetch automatique au scroll (IntersectionObserver) → 1er click instant
 *
 * ToastManager : affiche un toast en haut a droite apres add-to-cart.
 *   "✓ Ajoute" + image + titre + "Voir le panier" → ouvre cart drawer.
 *
 * Pattern Manager (cf. .claude/rules/javascript.md).
 */

/*********************************************************************************************
 * 🍞 TOAST MANAGER
 */
const ToastManager = {
	config: {
		selector: '[data-toast]',
		autoDismissMs: 3000,
	},

	state: {
		el: null,
		timer: null,
	},

	init() {
		this.state.el = document.querySelector(this.config.selector);
		if (!this.state.el) return;
		this._bindEvents();
		window.log('info', '🍞 [TOAST]', 'Initialized');
	},

	_bindEvents() {
		this.state.el.addEventListener('click', (e) => {
			if (e.target.closest('[data-toast-close]')) this.hide();
		});
	},

	/**
	 * Affiche le toast pour un produit ajoute.
	 * @param {{image: string, title: string}} payload
	 */
	show({ image, title }) {
		const el = this.state.el;
		if (!el) return;

		const imgEl = el.querySelector('[data-toast-image]');
		const titleEl = el.querySelector('[data-toast-title]');
		if (imgEl) imgEl.src = image || '';
		if (titleEl) titleEl.textContent = title || '';

		el.hidden = false;
		void el.offsetWidth;
		el.classList.add('is-visible');

		clearTimeout(this.state.timer);
		this.state.timer = setTimeout(() => this.hide(), this.config.autoDismissMs);
	},

	hide() {
		const el = this.state.el;
		if (!el) return;
		el.classList.remove('is-visible');
		clearTimeout(this.state.timer);
		setTimeout(() => { if (!el.classList.contains('is-visible')) el.hidden = true; }, 350);
	},
};

/*********************************************************************************************
 * 🛒 QUICK VIEW MANAGER
 */
const QuickViewManager = {
	config: {
		selectors: {
			card:        '[data-grid-card]',
			quickAdd:    '[data-quick-add]',
			panel:       '[data-panel="quick-view"]',
			content:     '[data-quick-view-content]',
			variantInput:'[data-quick-view-input]',
			submitBtn:   '[data-quick-view-submit]',
			variantId:   '[data-quick-view-variant-id]',
			variantsData:'[data-quick-view-variants]',
			form:        '[data-type="quick-view-add-form"]',
			reload:      '[data-quick-view-reload]',
		},
		prefetchRootMargin: '200px',
		maxPrefetchVariants: 12,
	},

	state: {
		cache: new Map(), // handle (ou handle?variant=ID) → htmlString
		inflight: new Map(), // handle → Promise (dedupe simultaneous fetches)
		currentHandle: null,
		reloadRequestId: 0, // ignore les reponses perimees (clics rapides sur plusieurs variantes)
	},

	init() {
		this._bindEvents();
		this._initPrefetchObserver();
		window.log('info', '🛒 [QUICK_VIEW]', 'Initialized');
	},

	_bindEvents() {
		document.addEventListener('click', (e) => {
			const quickAdd = e.target.closest(this.config.selectors.quickAdd);
			if (quickAdd) {
				e.preventDefault();
				const card = quickAdd.closest(this.config.selectors.card);
				if (card) this._handleQuickAdd(card);
			}
		});

		// Submit du form dans le panel
		document.addEventListener('submit', async (e) => {
			const form = e.target.closest(this.config.selectors.form);
			if (!form) return;
			e.preventDefault();
			await this._submitQuickViewForm(form);
		});

		// Variant change dans le panel : update hidden variant ID + dispo CTA
		document.addEventListener('change', (e) => {
			if (!e.target.matches(this.config.selectors.variantInput)) return;
			const panel = e.target.closest(this.config.selectors.panel);
			if (panel) this._syncQuickViewVariant(panel);
		});
	},

	/**
	 * IntersectionObserver : prefetch le markup quick-view des cards visibles.
	 */
	_initPrefetchObserver() {
		if (!('IntersectionObserver' in window)) return;
		const obs = new IntersectionObserver((entries) => {
			entries.forEach(entry => {
				if (entry.isIntersecting) {
					const handle = entry.target.dataset.productHandle;
					const count = parseInt(entry.target.dataset.variantsCount || '1', 10);
					if (handle && count > 1) this._prefetch(handle);
					obs.unobserve(entry.target);
				}
			});
		}, { rootMargin: this.config.prefetchRootMargin });

		document.querySelectorAll(this.config.selectors.card).forEach(card => obs.observe(card));
	},

	async _prefetch(handle) {
		if (this.state.cache.has(handle) || this.state.inflight.has(handle)) return;
		const promise = this._fetchMarkup(handle).catch(() => null);
		this.state.inflight.set(handle, promise);
		const html = await promise;
		this.state.inflight.delete(handle);
		if (html) this.state.cache.set(handle, html);
	},

	async _fetchMarkup(handle, variantId = null, priority = 'low') {
		/* Prefixe avec window.Shopify.routes.root pour respecter la locale courante
		 * (= '/' sur la langue primaire, '/en/' sur EN, etc.). Sans ca, le quick-view
		 * fetche le HTML en langue primaire (FR) meme sur les pages /en/.
		 * Le template product.quick-view.json est en "layout": false → seul le markup de la section. */
		const root = window.Shopify?.routes?.root || '/';
		const variantParam = variantId ? `&variant=${variantId}` : '';
		const res = await fetch(`${root}products/${handle}?view=quick-view${variantParam}`, {
			headers: { Accept: 'text/html' },
			priority,
		});
		if (!res.ok) throw new Error(`Quick-view fetch failed: ${res.status}`);
		return res.text();
	},

	/**
	 * Markup quick-view d'une variante : cache → fetch en cours (prefetch) → nouveau fetch.
	 * priority 'high' pour un clic utilisateur, 'low' pour le prefetch a l'ouverture.
	 */
	_getVariantMarkup(handle, variantId, priority = 'high') {
		const key = `${handle}?variant=${variantId}`;
		if (this.state.cache.has(key)) return Promise.resolve(this.state.cache.get(key));
		if (this.state.inflight.has(key)) return this.state.inflight.get(key);
		const promise = this._fetchMarkup(handle, variantId, priority)
			.then((html) => { this.state.cache.set(key, html); return html; })
			.finally(() => this.state.inflight.delete(key));
		this.state.inflight.set(key, promise);
		return promise;
	},

	/**
	 * Prefetch (basse priorite) des autres variantes a l'ouverture : le changement de couleur
	 * est ensuite instantane (cache). Plafonne pour les produits a beaucoup de variantes.
	 */
	_prefetchVariants(panel) {
		const handle = panel.querySelector(this.config.selectors.form)?.dataset.productHandle;
		const currentId = panel.querySelector(this.config.selectors.variantId)?.value;
		let variants = [];
		try { variants = JSON.parse(panel.querySelector(this.config.selectors.variantsData)?.textContent || '[]'); } catch (_) { return; }
		if (!handle || variants.length > this.config.maxPrefetchVariants) return;
		variants.forEach((v) => {
			if (String(v.id) !== currentId) this._getVariantMarkup(handle, v.id, 'low').catch(() => {});
		});
	},

	_handleQuickAdd(card) {
		const count = parseInt(card.dataset.variantsCount || '1', 10);
		if (count <= 1) {
			this._addDefaultVariant(card);
		} else {
			this._openQuickView(card);
		}
	},

	/**
	 * Cas 1 : ajout direct (1 seule variante).
	 */
	async _addDefaultVariant(card) {
		const variantId = card.dataset.defaultVariantId;
		if (!variantId) return;
		const button = card.querySelector(this.config.selectors.quickAdd);
		button?.classList.add('is-loading');
		try {
			await this._addToCart(variantId, {
				...(card.dataset.precommande === 'true' && { _precommande: 'true' }),
				// Disponibilite au moment de l'achat, affichee dans l'espace client
				...(card.dataset.disponibilite && { _disponibilite: card.dataset.disponibilite }),
			});
			ToastManager.show({
				image: card.dataset.productImage,
				title: card.dataset.productTitle,
			});
		} catch (err) {
			window.log('error', '🛒 [QUICK_VIEW]', 'Add default variant failed', { error: err.message });
		} finally {
			button?.classList.remove('is-loading');
		}
	},

	/**
	 * Cas 2 : ouvre le panel + injecte le markup quick-view (depuis cache ou fetch).
	 */
	async _openQuickView(card) {
		const handle = card.dataset.productHandle;
		if (!handle) return;
		const panel = document.querySelector(this.config.selectors.panel);
		const contentZone = panel?.querySelector('[data-panel-body]');
		if (!panel || !contentZone) return;

		this.state.currentHandle = handle;

		// Skeleton pendant le fetch
		contentZone.innerHTML = this._renderSkeleton();
		window.MMProductPanels?.open?.('quick-view', card.querySelector(this.config.selectors.quickAdd));

		try {
			let html = this.state.cache.get(handle);
			if (!html) html = await this._fetchMarkup(handle, null, 'high');
			this.state.cache.set(handle, html);

			// Le HTML retourne est une page complete. On extrait la zone .mm-quick-view.
			const parser = new DOMParser();
			const doc = parser.parseFromString(html, 'text/html');
			const fragment = doc.querySelector(this.config.selectors.content);
			if (!fragment) throw new Error('quick-view content not found in response');

			contentZone.innerHTML = '';
			contentZone.appendChild(fragment);
			// Le markup initial est celui de la variante par defaut : evite un fetch au retour sur cette variante
			const initialId = panel.querySelector(this.config.selectors.variantId)?.value;
			if (initialId) this.state.cache.set(`${handle}?variant=${initialId}`, html);
			window.initButtonCharacterStagger?.();
			// Sync initial : cross-availability des options sur la variante par defaut
			this._syncQuickViewVariant(panel);
			this._prefetchVariants(panel);
		} catch (err) {
			window.log('error', '🛒 [QUICK_VIEW]', 'Failed to load quick-view', { handle, error: err.message });
			/* Message d'erreur lu depuis data-i18n-load-error sur [data-panel="quick-view"] (i18n via Liquid) */
			const errMsg = panel.dataset.i18nLoadError || 'Erreur de chargement.';
			contentZone.innerHTML = `<p class="text-sm mm-black-400">${errMsg}</p>`;
		}
	},

	_renderSkeleton() {
		return `
			<div class="mm-quick-view-skeleton" aria-busy="true">
				<div class="mm-skel-line" style="width: 40%;"></div>
				<div class="mm-skel-line" style="width: 80%;"></div>
				<div class="mm-skel-line" style="width: 60%;"></div>
				<div class="mm-skel-block"></div>
				<div class="mm-skel-block"></div>
			</div>
		`;
	},

	/**
	 * Synchronise l'ID variante hidden quand l'utilisateur change une option dans le panel.
	 */
	_syncQuickViewVariant(panel) {
		const variantsScript = panel.querySelector(this.config.selectors.variantsData);
		if (!variantsScript) return;
		let variants = [];
		try { variants = JSON.parse(variantsScript.textContent); } catch (_) { return; }

		// 1. Cross-availability d'abord (set disabled sur les options indispos)
		this._updateCrossAvailability(panel, variants);

		// 2. Auto-switch : si la valeur checkee d'une option non-couleur est devenue disabled,
		// bascule sur la 1ere dispo. Garantit qu'on calcule matched sur une combo VALIDE.
		this._autoSwitchToAvailable(panel);

		// 3. Compute selected APRES auto-switch
		const selected = {};
		panel.querySelectorAll('fieldset.mm-quick-view-option').forEach(fs => {
			const pos = parseInt(fs.dataset.optionPosition, 10);
			const checked = fs.querySelector(`${this.config.selectors.variantInput}:checked`);
			if (pos && checked) selected[pos] = checked.value;
		});

		const matched = variants.find(v => {
			return (!selected[1] || v.option1 === selected[1]) &&
				(!selected[2] || v.option2 === selected[2]) &&
				(!selected[3] || v.option3 === selected[3]);
		});

		const hidden = panel.querySelector(this.config.selectors.variantId);
		const form = panel.querySelector(this.config.selectors.form);
		const renderedId = hidden?.value;
		if (matched && hidden) hidden.value = matched.id;
		// Sync l'image utilisee par le toast sur la variante selectionnee
		if (matched && form) {
			const src = matched.featured_image?.src || matched.featured_image;
			if (src) {
				const sep = src.includes('?') ? '&' : '?';
				form.dataset.productImage = `${src}${sep}width=200`;
			}
		}

		// 4. Prix, couleur, CTA, stock/livraison : re-rendus cote serveur (comme le reload du PDP).
		// Pas de fetch a l'ouverture : le markup initial correspond deja a la variante checkee.
		if (matched && String(matched.id) !== renderedId) this._reloadVariant(panel, matched.id);
	},

	/**
	 * Re-rend les zones [data-quick-view-reload] pour la variante (fetch ?view=quick-view&variant=ID) :
	 * meme principe que updateReloadElements du PDP, la logique de dispo/CTA/livraison reste en Liquid.
	 */
	async _reloadVariant(panel, variantId) {
		const handle = panel.querySelector(this.config.selectors.form)?.dataset.productHandle;
		if (!handle) return;
		const requestId = ++this.state.reloadRequestId;

		// Etat chargement (CSS : opacite + CTA non cliquable) — invisible si la variante est deja en cache,
		// le remplacement se faisant avant le prochain rendu.
		const zones = panel.querySelectorAll(this.config.selectors.reload);
		zones.forEach((el) => el.setAttribute('aria-busy', 'true'));

		try {
			const html = await this._getVariantMarkup(handle, variantId);
			if (requestId !== this.state.reloadRequestId) return;

			const doc = new DOMParser().parseFromString(html, 'text/html');
			zones.forEach((el) => {
				const next = doc.querySelector(`[data-quick-view-reload="${el.dataset.quickViewReload}"]`);
				if (next) el.replaceWith(next);
				else el.removeAttribute('aria-busy');
			});
			window.initButtonCharacterStagger?.();
		} catch (err) {
			if (requestId === this.state.reloadRequestId) zones.forEach((el) => el.removeAttribute('aria-busy'));
			window.log('error', '🛒 [QUICK_VIEW]', 'Variant reload failed', { variantId, error: err.message });
		}
	},

	/**
	 * Bascule sur la 1ere valeur dispo si la valeur checkee d'une option non-couleur
	 * est devenue disabled (pattern identique au _updateNonColorOptionsAvailability du PDP).
	 */
	_autoSwitchToAvailable(panel) {
		const colorInput = Array.from(panel.querySelectorAll(`${this.config.selectors.variantInput}:checked`))
			.find(r => /couleur|color/i.test(r.name));

		panel.querySelectorAll('fieldset.mm-quick-view-option').forEach(fs => {
			// Skip la fieldset couleur
			if (colorInput && fs.contains(colorInput)) return;

			const checked = fs.querySelector(`${this.config.selectors.variantInput}:checked`);
			if (!checked || !checked.disabled) return; // ok, valeur courante encore dispo

			const firstAvailable = fs.querySelector(`${this.config.selectors.variantInput}:not(:disabled)`);
			if (firstAvailable) {
				checked.checked = false;
				firstAvailable.checked = true;
			}
		});
	},

	/**
	 * Pour chaque option non-couleur, barre les valeurs qui n'ont pas de variante dispo
	 * avec la couleur actuellement selectionnee (meme logique que le PDP).
	 */
	_updateCrossAvailability(panel, variants) {
		// Detecte la couleur via le nom du radio (contient 'couleur' ou 'color')
		const colorInput = Array.from(panel.querySelectorAll(`${this.config.selectors.variantInput}:checked`))
			.find(r => /couleur|color/i.test(r.name));
		if (!colorInput) return;
		const colorValue = colorInput.value;

		panel.querySelectorAll('fieldset.mm-quick-view-option').forEach(fs => {
			// On skip la fieldset couleur (celle qui contient le colorInput)
			if (fs.contains(colorInput)) return;

			fs.querySelectorAll('.mm-quick-view-size-pill, .mm-quick-view-color-swatch').forEach(pill => {
				const input = pill.querySelector(this.config.selectors.variantInput);
				const value = input?.value;
				if (!value) return;
				const matching = variants.find(v => {
					const opts = [v.option1, v.option2, v.option3];
					return opts.includes(colorValue) && opts.includes(value);
				});
				const isAvailable = matching && matching.available !== false;
				input.disabled = !isAvailable;
				pill.classList.toggle('mm-quick-view-size-pill--soldout', !isAvailable);
			});
		});
	},

	async _submitQuickViewForm(form) {
		const submitBtn = form.querySelector(this.config.selectors.submitBtn);
		const variantId = form.querySelector(this.config.selectors.variantId)?.value;
		if (!variantId) return;

		// Line item properties rendues cote serveur selon la variante (ex: _precommande)
		const properties = {};
		new FormData(form).forEach((value, key) => {
			const name = key.match(/^properties\[(.+)\]$/)?.[1];
			if (name) properties[name] = value;
		});

		submitBtn?.classList.add('mm-loading');
		submitBtn && (submitBtn.disabled = true);
		try {
			await this._addToCart(variantId, properties);
			ToastManager.show({
				image: form.dataset.productImage,
				title: form.dataset.productTitle,
			});
			window.MMProductPanels?.close?.();
		} catch (err) {
			window.log('error', '🛒 [QUICK_VIEW]', 'Submit failed', { error: err.message });
		} finally {
			submitBtn?.classList.remove('mm-loading');
			submitBtn && (submitBtn.disabled = false);
		}
	},

	/**
	 * POST /cart/add.js + refresh UI cart (count, drawer si visible).
	 * Ne PAS ouvrir le drawer (vs initAddToCartForms classique).
	 * @param {string} variantId
	 * @param {Object<string, string>} [properties] - line item properties (ex: { _precommande: 'true' })
	 */
	async _addToCart(variantId, properties = {}) {
		const formData = new FormData();
		formData.append('id', variantId);
		formData.append('quantity', '1');
		Object.entries(properties).forEach(([key, value]) => formData.append(`properties[${key}]`, value));

		const sectionId = window.mmGetCartSectionId?.();
		if (sectionId) formData.append('sections', sectionId);

		const res = await fetch('/cart/add.js', { method: 'POST', body: formData });
		if (!res.ok) throw new Error(`cart/add failed: ${res.status}`);
		const data = await res.json();

		window.mmRefreshCartUI?.(data);
		/* Publie le standard event Shopify Web Pixels pour que Addingwell/GA4/Meta
		 * captent l'add-to-cart depuis le quickview (sinon zero signal cote ads). */
		window.mmPublishCartAdd?.(data);
		document.dispatchEvent(new CustomEvent('cart:item-added-quickview', { detail: data, bubbles: true }));
		return data;
	},
};

/*********************************************************************************************
 * 🚀 INIT
 * Note : la wishlist est geree par mm_wishlist.js (app Wishlist Power / Maestrooo).
 */
if (document.readyState === 'loading') {
	document.addEventListener('DOMContentLoaded', () => {
		ToastManager.init();
		QuickViewManager.init();
	});
} else {
	ToastManager.init();
	QuickViewManager.init();
}

window.QuickViewManager = QuickViewManager;
window.ToastManager = ToastManager;
